import React, {
  useMemo,
  useState,
  useEffect,
} from "react";

import {
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";

import { Eye } from "lucide-react";

import { Button } from "react-bootstrap";

import { saveAs } from "file-saver";

import {
  RiDownloadFill,
} from "react-icons/ri";

import {
  MdOutlineBlock,
  MdOutlineDeleteOutline,
} from "react-icons/md";

import { FaArrowLeft } from "react-icons/fa6";

import { SiTicktick } from "react-icons/si";

import { ImConfused } from "react-icons/im";

import {
  Document,
  Packer,
  Paragraph,
  TextRun,
} from "docx";

import ApiService from "../../../nhrc_apis/ApiService";

const { JobAPI } = ApiService;

function AdminResponses() {

  const { jobId } = useParams();

  const navigate = useNavigate();

  const location = useLocation();

  /* =========================================
     STATES
  ========================================= */

  const [candidates, setCandidates] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [selectedIds, setSelectedIds] =
    useState([]);

  const [statusFilter, setStatusFilter] =
    useState("all");

  const [nameFilter, setNameFilter] =
    useState("");

  const [skillFilter, setSkillFilter] =
    useState("");

  const [emailFilter, setEmailFilter] =
    useState("");

  const [page, setPage] =
    useState(1);

  const [jobTitle, setJobTitle] =
    useState("Unknown Position");

  const pageSize = 10;

  /* =========================================
     LOAD APPLICATIONS
  ========================================= */

  useEffect(() => {

    if (jobId) {

      fetchApplications();
    }

  }, [jobId]);

  const fetchApplications = async () => {

    try {

      setLoading(true);

      console.log("JOB ID :", jobId);

      const response =
        await JobAPI.getApplicationsByJob(jobId);

      console.log(
        "FULL APPLICATION RESPONSE :",
        response
      );

      console.log(
        "APPLICATION RESPONSE DATA :",
        response?.data
      );

      let applications = [];

      /* =========================================
         HANDLE ALL RESPONSE TYPES
      ========================================= */

      if (
        Array.isArray(response?.data)
      ) {

        applications =
          response.data;

      } else if (
        Array.isArray(
          response?.data?.applications
        )
      ) {

        applications =
          response.data.applications;

      } else if (
        Array.isArray(
          response?.data?.data
        )
      ) {

        applications =
          response.data.data;

      } else {

        applications = [];
      }

      console.log(
        "FINAL APPLICATIONS :",
        applications
      );

      /* =========================================
         FORMAT DATA
      ========================================= */

      const formatted =
        applications.map(
          (item, index) => {

            return {

              id:
                item?.id ||
                index + 1,

              firstName:
                item?.first_name ||
                "N/A",

              lastName:
                item?.last_name ||
                "",

              email:
                item?.email ||
                "N/A",

              phone:
                item?.phone_number ||
                "N/A",

              keySkills:
                item?.key_skills ||
                "N/A",

              status:
                item?.status ||
                "NEW",

              resume:
                item?.resume_file ||
                "",

              location:
                item?.location ||
                "",

              qualification:
                item?.highest_qualification ||
                "",

              experienceType:
                item?.experience_type ||
                "",

              appliedAt:
                item?.applied_at ||
                "",

              fullData: item,
            };
          }
        );

      console.log(
        "FORMATTED DATA :",
        formatted
      );

      setCandidates(formatted);

    } catch (error) {

      console.log(
        "GET APPLICATION ERROR :",
        error
      );

      console.log(
        "ERROR RESPONSE :",
        error?.response?.data
      );

      alert(
        "Failed to load applications"
      );

    } finally {

      setLoading(false);
    }
  };

  /* =========================================
     JOB TITLE
  ========================================= */

  useEffect(() => {

    if (
      location.state?.jobTitle
    ) {

      setJobTitle(
        location.state.jobTitle
      );
    }

  }, [location.state]);

  /* =========================================
     FILTERS
  ========================================= */

  const filteredAll = useMemo(() => {

    return candidates.filter((c) => {

      if (
        statusFilter !== "all" &&
        c.status?.toLowerCase() !==
          statusFilter.toLowerCase()
      ) {

        return false;
      }

      if (
        nameFilter &&
        !`${c.firstName} ${c.lastName}`
          .toLowerCase()
          .includes(
            nameFilter.toLowerCase()
          )
      ) {

        return false;
      }

      if (
        skillFilter &&
        !c.keySkills
          ?.toLowerCase()
          .includes(
            skillFilter.toLowerCase()
          )
      ) {

        return false;
      }

      if (
        emailFilter &&
        !c.email
          ?.toLowerCase()
          .includes(
            emailFilter.toLowerCase()
          )
      ) {

        return false;
      }

      return true;
    });

  }, [
    candidates,
    statusFilter,
    nameFilter,
    skillFilter,
    emailFilter,
  ]);

  /* =========================================
     PAGINATION
  ========================================= */

  const totalPages = Math.max(
    1,
    Math.ceil(
      filteredAll.length / pageSize
    )
  );

  const paged = useMemo(() => {

    const start =
      (page - 1) * pageSize;

    return filteredAll.slice(
      start,
      start + pageSize
    );

  }, [filteredAll, page]);

  /* =========================================
     SELECT
  ========================================= */

  const allSelected =
    paged.length > 0 &&
    paged.every((c) =>
      selectedIds.includes(c.id)
    );

  const toggleSelectAll = (e) => {

    if (e.target.checked) {

      setSelectedIds(
        paged.map((c) => c.id)
      );

    } else {

      setSelectedIds([]);
    }
  };

  const toggleSelect = (id) => {

    setSelectedIds((prev) => {

      if (prev.includes(id)) {

        return prev.filter(
          (x) => x !== id
        );

      } else {

        return [...prev, id];
      }
    });
  };

  /* =========================================
     STATUS BADGE
  ========================================= */

  const getStatusBadgeClass = (
    status = ""
  ) => {

    const s =
      status.toLowerCase();

    if (s === "shortlisted")
      return "bg-success";

    if (s === "rejected")
      return "bg-danger";

    if (s === "maybe")
      return "bg-warning";

    return "bg-secondary";
  };

  /* =========================================
     STATUS UPDATE
  ========================================= */

  const bulkUpdateStatus =
    async (status) => {

      try {

        for (const id of selectedIds) {

          if (
            status ===
            "Shortlisted"
          ) {

            await JobAPI.shortlistApplication(
              id
            );

          } else if (
            status ===
            "Rejected"
          ) {

            await JobAPI.rejectApplication(
              id
            );
          }
        }

        const updated =
          candidates.map((c) =>

            selectedIds.includes(c.id)

              ? {
                  ...c,
                  status,
                }

              : c
          );

        setCandidates(updated);

        setSelectedIds([]);

      } catch (error) {

        console.log(
          "STATUS UPDATE ERROR :",
          error
        );

        alert(
          "Failed to update status"
        );
      }
    };

  /* =========================================
     DELETE
  ========================================= */

  const handleDelete = () => {

    if (
      selectedIds.length === 0
    ) {

      return alert(
        "Please select candidates"
      );
    }

    const updated =
      candidates.filter(
        (c) =>
          !selectedIds.includes(
            c.id
          )
      );

    setCandidates(updated);

    setSelectedIds([]);
  };

  /* =========================================
     DOWNLOAD
  ========================================= */

  const handleDownloadSelected =
    async () => {

      const selected =
        candidates.filter((c) =>
          selectedIds.includes(
            c.id
          )
        );

      const doc =
        new Document({

          sections:
            selected.map((c) => ({

              children: [

                new Paragraph({

                  children: [

                    new TextRun({

                      text:
                        "Candidate Details",

                      bold: true,

                      size: 28,
                    }),
                  ],
                }),

                new Paragraph(
                  `Name : ${c.firstName} ${c.lastName}`
                ),

                new Paragraph(
                  `Email : ${c.email}`
                ),

                new Paragraph(
                  `Phone : ${c.phone}`
                ),

                new Paragraph(
                  `Skills : ${c.keySkills}`
                ),

                new Paragraph(
                  `Status : ${c.status}`
                ),
              ],
            })),
        });

      const blob =
        await Packer.toBlob(
          doc
        );

      saveAs(
        blob,
        "candidates.docx"
      );
    };

  /* =========================================
     VIEW CANDIDATE
  ========================================= */

  const handleViewCandidate = (
    candidate
  ) => {

   navigate(
  `/dashboard/Adminjobproview/${candidate.id}`,
  {
    state: {
      candidate,
    },
  }
);
  };

  /* =========================================
     UI
  ========================================= */

  return (

    <div className="container-fluid py-3 mt-5">

      <div className="d-flex justify-content-between align-items-center mb-3">

        <div>

          <button
            className="btn btn-outline-secondary btn-sm mb-2"
            onClick={() =>
              navigate(-1)
            }
          >

            <FaArrowLeft />
            {" "}
            Back

          </button>

          <h5>
            Candidates Responses
          </h5>

          <small className="text-muted">

            Position :
            {" "}

            <span className="fw-bold">

              {jobTitle}

            </span>

          </small>

        </div>

      </div>

      {/* LOADING */}

      {loading ? (

        <div className="text-center py-5">

          <div className="spinner-border text-primary" />

        </div>

      ) : (

        <>

          {/* TABLE */}

          <table className="table table-bordered table-hover">

            <thead>

              <tr>

                <th>

                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                  />

                </th>

                <th>Name</th>

                <th>Email</th>

                <th>Phone</th>

                <th>Skills</th>

                <th>Status</th>

                <th>Action</th>

              </tr>

            </thead>

            <tbody>

              {paged.length > 0 ? (

                paged.map((c) => (

                  <tr key={c.id}>

                    <td>

                      <input
                        type="checkbox"
                        checked={selectedIds.includes(
                          c.id
                        )}
                        onChange={() =>
                          toggleSelect(c.id)
                        }
                      />

                    </td>

                    <td>
                      {c.firstName} {c.lastName}
                    </td>

                    <td>
                      {c.email}
                    </td>

                    <td>
                      {c.phone}
                    </td>

                    <td>
                      {c.keySkills}
                    </td>

                    <td>

                      <span
                        className={`badge ${getStatusBadgeClass(
                          c.status
                        )}`}
                      >

                        {c.status}

                      </span>

                    </td>

                    <td>

                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() =>
                          handleViewCandidate(
                            c
                          )
                        }
                      >

                        <Eye size={14} />
                        {" "}
                        View

                      </button>

                    </td>

                  </tr>
                ))

              ) : (

                <tr>

                  <td
                    colSpan="7"
                    className="text-center py-4"
                  >

                    No Candidates Found

                  </td>

                </tr>
              )}

            </tbody>

          </table>

        </>
      )}

    </div>
  );
}

export default AdminResponses;